import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Paper,
  FormLabel,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import InfoIcon from "@mui/icons-material/Info";
import {
  golfTooltipHeaders,
  originsTooltip,
} from "../../../constants/TooltipInformation";
import {
  validateDiscountTypeField,
  validationMessages,
  MIN_TEXT_FIELD_LENGTH,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const DiscountTypes = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [discType, setDiscType] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [nameErrorMessage, setNameErrorMessage] = useState<string>("");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [GolfDiscountTypes, setGolfDiscountTypes] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateInput = () => {
    if (!discType || discType.trim().length === 0) {
      setAlertMessage("Discount Type is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    setNameError(false);
    setNameErrorMessage("");

    if (discType.trim().length < MIN_TEXT_FIELD_LENGTH) {
      setNameError(true);
      setNameErrorMessage(validationMessages.discountTypeMinLength);
      return false;
    }

    if (!validateDiscountTypeField(discType)) {
      setNameError(true);
      setNameErrorMessage(validationMessages.discountTypes);
      return false;
    }

    return true;
  };

  // useEffect(() => {
  //   validateInput();
  // }, [discType]);

  // useEffect(() => {
  //   if (fieldValues && fieldValues.OutletName !== null) {
  //     setDiscType(fieldValues.);
  //   }
  // }, []);

  const updateGolfDiscountTypes = () => {
    if (!validateInput()) return false;
    if (
      checkDuplicate(
        GolfDiscountTypes.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.DISCOUNTTYPE,
        discType,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    setGolfDiscountTypes((prev: any) => {
      return prev.map((item: any) => {
        if (item.id === selectedId) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editGolfDiscountTypes(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${GolfPropertyEnum.discountTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfDiscountTypes = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(GolfDiscountTypes, DNDFieldsEnum.DISCOUNTTYPE, discType)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfDiscountTypes((prev: any) => {
      return updateData(
        prev !== null
          ? [
              ...prev,
              {
                id: GolfDiscountTypes.length + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editGolfDiscountTypes(null);
    setEditClicked(false);
    setActive(true);
  };

  const editGolfDiscountTypes = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setDiscType(list?.["Discount Type"]);
    setActive(list?.Active);
    setNameError(false);
    setNameErrorMessage("");
  };

  const deleteGolfDiscountTypes = (id: number) => {
    setGolfDiscountTypes(deleteItem(GolfDiscountTypes, id));
  };

  useEffect(() => {
    fieldValues && setGolfDiscountTypes(fieldValues);
  }, []);

  useEffect(() => {
    data(GolfDiscountTypes);
    editClicked &&
      setSelectedId(
        checkData(GolfDiscountTypes, selectedId) ? selectedId : null,
      );
  }, [GolfDiscountTypes]);

  const payload = {
    "Discount Type": discType,
    Active: active,
  };

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Discount Types
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.discountTypes)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editGolfDiscountTypes(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editGolfDiscountTypes(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            width="100%"
            gap="20px"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Discount Types
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.discountType)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="DisocuntTypes"
                variant="outlined"
                placeholder="Discount Type"
                helperText={nameError ? nameErrorMessage : ""}
                error={nameError}
                onChange={(e: any) => {
                  setDiscType(e.target.value);
                  setNameError(false);
                  setNameErrorMessage("");
                }}
                value={discType ?? ""}
                sx={{ width: "100%" }}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            gap="50px"
            width="100%"
          >
            <Stack direction="row" alignItems="center" gap="4px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              <Tooltip title={tooltipComponent(originsTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updateGolfDiscountTypes() : addGolfDiscountTypes()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Discount Types
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Discount Types Available</Typography>
            <Stack direction="column" gap="10px">
              {GolfDiscountTypes ? (
                <DraggableItemList
                  itemData={GolfDiscountTypes}
                  onOrderUpdate={(order) => setGolfDiscountTypes(order)}
                  onEdit={(info) => editGolfDiscountTypes(info)}
                  onDelete={(id) => deleteGolfDiscountTypes(id)}
                  code={DNDFieldsEnum.DISCOUNTTYPE}
                  name={DNDFieldsEnum.ACTIVE}
                />
              ) : (
                <Typography variant="body2">
                  No Discount Types Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
        <AlertComponent
          message={alertMessage}
          type={alertType}
          alertOpen={openAlert}
          closeAlert={() => setOpenAlert(false)}
        />
      </Stack>
    </Stack>
  );
};

export default DiscountTypes;
